import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons, ImageField } from '@barboxx/admin-components';
import { SERVING_CONTAINER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ServingContainer } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    ImageKitCustomMetadataKeys,
    ImageKitFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
} from '@barboxx/types';

import { REACT_APP_ENV } from '../../../../../environments/environment';

interface Props {
    servingContainer: ServingContainer.DetailsResponse;
}

export const ServingContainerDetailsCard = ({ servingContainer }: Props) => {
    const { ...rest } = servingContainer;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<ServingContainer.UpdateRequest>({
        resolver: yupResolver(SERVING_CONTAINER_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['servingContainer', servingContainer.id],
        mutationFn: (data: ServingContainer.UpdateRequest) =>
            Api.Servings.ServingContainer.putServingContainer(
                servingContainer.id,
                data
            ),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: ServingContainer.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated serving container!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset();
            })
            .catch(e => {
                notifications.show('Error Updating serving container!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as ServingContainer;

    return (
        <FormContainer
            onSuccess={onSubmit}
            formContext={formContext}
            progressive
        >
            <Card>
                <CardHeader title="Serving Container Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            label="Name"
                            name="name"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            fullWidth
                            name="volumeOz"
                            label="Volume (oz)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 1000,
                                    step: 0.1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <TextFieldElement
                            fullWidth
                            name="heightMm"
                            label="Height (mm)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 300,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <TextFieldElement
                            fullWidth
                            name="diameterMm"
                            label="Diameter (mm)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 300,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <TextFieldElement
                            fullWidth
                            name="widthMm"
                            label="Width (mm)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 300,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <TextFieldElement
                            fullWidth
                            name="depthMm"
                            label="Depth (mm)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 300,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <ImageField
                            label="Image"
                            name="image"
                            readOnly={!editable}
                            imagekitUploadProps={{
                                width: 200,
                                fileName: `${values.id}`,
                                folder: ImageKitFolders.ServingContainers,
                                env: REACT_APP_ENV,
                                customMetadata: {
                                    [ImageKitCustomMetadataKeys.Id]: `${values['id']}`,
                                    [ImageKitCustomMetadataKeys.Type]:
                                        ImageKitTypes.ServingContainer,
                                    [ImageKitCustomMetadataKeys.Env]:
                                        REACT_APP_ENV,
                                },
                                tags: [
                                    values['name'],
                                    ImageKitTypes.ServingContainer,
                                    `${values['id']}`,
                                    REACT_APP_ENV,
                                ],
                            }}
                            imagekitProps={{
                                width: 200,
                                transformation: [
                                    {
                                        raw: ImageKitNamedTransforms.Web.square,
                                    },
                                ],
                                loading: 'lazy',
                                lqip: {
                                    raw: ImageKitNamedTransforms.Web.squareLq,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="ServingContainer">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
